import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/dashboard/PageHeader';
import JobsStatsBar from '@/components/jobs/JobsStatsBar';
import JobsBoard, { JobsBoardSkeleton } from '@/components/jobs/JobsBoard';
import CreateJobDialog from '@/components/jobs/CreateJobDialog';
import JobDetailSheet from '@/components/jobs/JobDetailSheet';
import { useIsWideScreen } from '@/hooks/use-media-query';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  Search,
  RefreshCw,
  Plus,
  X,
  HardHat,
  Archive,
  ArrowLeft,
} from 'lucide-react';
import {
  computeJobsStats,
  applyJobFilter,
  isJobArchived,
} from '@/lib/jobsData';
import { useData } from '@/context/DataContext';

export default function ActiveJobsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isWide = useIsWideScreen();
  // True when WE pushed the ?jobId entry (opened from the list), so closing
  // can pop it with history back instead of stacking a new list entry —
  // the phone's Back button then goes job → list, never list → job.
  const pushedJobRef = React.useRef(false);

  // ── Shared cache (lazy-loaded once across the dashboard) ────────────────
  const {
    jobs,
    jobsLoading,
    jobsError,
    hasLoadedJobs,
    clients,
    clientsLoading,
    ensureJobs,
    ensureClients,
    refreshJobs,
    refreshClients,
    upsertJobInCache,
    removeJobFromCache,
  } = useData();

  // Kick off lazy loads. The provider dedupes so concurrent pages share
  // a single round-trip.
  useEffect(() => {
    ensureJobs();
    ensureClients();
  }, [ensureJobs, ensureClients]);

  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('phase-desc');
  const [createOpen, setCreateOpen] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  // View toggle — 'active' (default) shows non-archived jobs, 'archived'
  // surfaces completed/closed jobs in a read-mostly archive view.
  const [view, setView] = useState('active');

  // Page is "loading" only on the very first cache fill (subsequent visits
  // are instant from cache).
  const loading = !hasLoadedJobs && jobsLoading;
  const error = jobsError;

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([refreshJobs(), refreshClients()]);
    } catch (_) {
      /* error already surfaced via context state */
    } finally {
      setRefreshing(false);
    }
  }, [refreshJobs, refreshClients]);

  // Derive the active job from the cache, so when the cache refreshes
  // the detail sheet automatically reflects fresh data.
  const activeJob = useMemo(
    () => (activeJobId ? jobs.find((j) => j.id === activeJobId) || null : null),
    [jobs, activeJobId]
  );

  // The open job lives in the URL (?jobId=<uuid>): deep links, reload, and
  // the phone's Back button all work off it. This effect is the one place
  // that turns the URL into open/closed state.
  useEffect(() => {
    if (loading) return;
    const targetId = searchParams.get('jobId');
    if (targetId) {
      const found = jobs.find((j) => j.id === targetId);
      if (found) {
        if (activeJobId !== found.id) setActiveJobId(found.id);
        if (!detailOpen) setDetailOpen(true);
        // Switch to the archive view if the linked job is archived so the
        // list behind it matches.
        if (isJobArchived(found)) setView('archived');
      } else {
        // Deleted, or not this account's job: drop the dead param.
        setSearchParams({}, { replace: true });
      }
    } else if (detailOpen) {
      setDetailOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, jobs, searchParams]);

  // Partition by archive status, then filter/sort within the chosen view.
  const { activeJobs, archivedJobs } = useMemo(() => {
    const active = [];
    const archived = [];
    for (const j of jobs) {
      if (isJobArchived(j)) archived.push(j);
      else active.push(j);
    }
    return { activeJobs: active, archivedJobs: archived };
  }, [jobs]);

  const sourceList = view === 'archived' ? archivedJobs : activeJobs;
  const filtered = useMemo(
    () => applyJobFilter(sourceList, { search, sort }),
    [sourceList, search, sort]
  );
  // Stats are computed over the currently-visible list so the bar reflects
  // the active view (active vs archived).
  const stats = useMemo(() => computeJobsStats(filtered), [filtered]);

  // Mutation reflectors — push changes straight into the shared cache so
  // every consumer (Dashboard, Job Costing, Clients & Leads, …) sees them
  // without a round-trip.
  const replaceJob = useCallback(
    (updated) => {
      if (!updated?.id) return;
      upsertJobInCache(updated);
    },
    [upsertJobInCache]
  );
  const removeJob = useCallback(
    (removed) => {
      if (!removed?.id) return;
      removeJobFromCache(removed.id);
      setActiveJobId((cur) => (cur === removed.id ? null : cur));
    },
    [removeJobFromCache]
  );
  const addJob = useCallback(
    (created) => {
      if (!created?.id) return;
      upsertJobInCache(created);
    },
    [upsertJobInCache]
  );

  const openJob = (job) => {
    if (!job?.id) return;
    setActiveJobId(job.id);
    setDetailOpen(true);
    pushedJobRef.current = true;
    setSearchParams({ jobId: job.id });
  };

  const closeJob = () => {
    setDetailOpen(false);
    if (!searchParams.get('jobId')) return;
    if (pushedJobRef.current) {
      pushedJobRef.current = false;
      navigate(-1);
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  // Wide screens: an open job takes the whole page (John, 2026-09-30 — the
  // 440px side panel was too cramped to work in). Narrow screens keep the
  // full-screen overlay they already had.
  const showFullPage = isWide && detailOpen && !!activeJob;

  // A job clicked far down the list should open at its top, not mid-page.
  useEffect(() => {
    if (showFullPage) window.scrollTo({ top: 0 });
  }, [showFullPage, activeJobId]);

  const isArchiveView = view === 'archived';

  if (showFullPage) {
    return (
      <div data-testid="active-jobs-page">
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={closeJob}
            className="text-muted-foreground hover:text-foreground uppercase tracking-[0.16em] text-xs"
            data-testid="job-full-page-back"
          >
            <ArrowLeft size={14} className="mr-2" />
            {t('jobs.back_to_jobs', { defaultValue: 'Back to jobs' })}
          </Button>
        </div>
        <JobDetailSheet
          job={activeJob}
          clients={clients}
          open
          onOpenChange={(o) => {
            if (!o) closeJob();
          }}
          onUpdated={replaceJob}
          onDeleted={removeJob}
          panelClassName="xl:static xl:max-h-none"
        />
        <CreateJobDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          clients={clients}
          onCreated={addJob}
        />
      </div>
    );
  }

  return (
    <div data-testid="active-jobs-page">
      <PageHeader
        eyebrow={t('jobs.eyebrow')}
        title={isArchiveView ? t('jobs.archive.title') : t('jobs.title')}
        subtitle={
          isArchiveView ? t('jobs.archive.subtitle') : t('jobs.subtitle')
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading || refreshing || clientsLoading}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="jobs-refresh-button"
            >
              <RefreshCw
                size={14}
                className={`mr-2 ${refreshing ? 'animate-spin' : ''}`}
              />
              {t('common.refresh')}
            </Button>
            {!isArchiveView ? (
              <Button
                size="sm"
                onClick={() => setCreateOpen(true)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                data-testid="jobs-create-button"
              >
                <Plus size={14} className="mr-2" />
                {t('jobs.create_button')}
              </Button>
            ) : null}
          </div>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="jobs-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      {/* Active / Archived view toggle */}
      <div className="mb-4">
        <Tabs value={view} onValueChange={setView} className="w-full">
          <TabsList
            className="bg-[hsl(var(--muted))] border border-border h-10 p-1"
            data-testid="jobs-view-tabs"
          >
            <TabsTrigger
              value="active"
              className="data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:border data-[state=active]:border-[hsl(var(--primary))]/30 text-muted-foreground uppercase tracking-[0.16em] text-xs"
              data-testid="jobs-view-tab-active"
            >
              <HardHat size={14} className="mr-1.5" />
              {t('jobs.tab_active')} ({activeJobs.length})
            </TabsTrigger>
            <TabsTrigger
              value="archived"
              className="data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:border data-[state=active]:border-[hsl(var(--primary))]/30 text-muted-foreground uppercase tracking-[0.16em] text-xs"
              data-testid="jobs-view-tab-archived"
            >
              <Archive size={14} className="mr-1.5" />
              {t('jobs.tab_archived')} ({archivedJobs.length})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <JobsStatsBar stats={stats} />

      <div className="mt-5 sm:mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative flex-1 lg:max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('jobs.search_placeholder')}
            className="h-10 pl-9 pr-9 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
            data-testid="jobs-search-input"
          />
          {search ? (
            <button
              type="button"
              aria-label={t('common.clear_search')}
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="jobs-search-clear"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger
            className="h-10 w-full lg:w-[220px] bg-[hsl(var(--muted))] border-border text-foreground"
            data-testid="jobs-sort"
          >
            <SelectValue placeholder={t('jobs.sort_placeholder')} />
          </SelectTrigger>
          <SelectContent className="bg-[hsl(var(--card))] border-border">
            <SelectItem value="phase-desc">{t('jobs.sort_phase_desc')}</SelectItem>
            <SelectItem value="phase-asc">{t('jobs.sort_phase_asc')}</SelectItem>
            <SelectItem value="value-desc">{t('jobs.sort_value_desc')}</SelectItem>
            <SelectItem value="name-asc">{t('jobs.sort_name_asc')}</SelectItem>
            <SelectItem value="name-desc">{t('jobs.sort_name_desc')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4">
        {loading ? (
          <JobsBoardSkeleton />
        ) : (
          <JobsBoard
            jobs={filtered}
            onOpenJob={openJob}
            onCreateJob={isArchiveView ? null : () => setCreateOpen(true)}
            search={search}
            archived={isArchiveView}
          />
        )}
        {/* Narrow screens: the job opens as a full-screen overlay. */}
        {!isWide && activeJob ? (
          <JobDetailSheet
            job={activeJob}
            clients={clients}
            open={detailOpen}
            onOpenChange={(o) => {
              if (o) setDetailOpen(true);
              else closeJob();
            }}
            onUpdated={replaceJob}
            onDeleted={removeJob}
          />
        ) : null}
      </div>

      <CreateJobDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        clients={clients}
        onCreated={addJob}
      />
    </div>
  );
}
